import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Loader2, Sparkles, Mic, FileText, Shield, Lock, KeyRound } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // 2FA email : second facteur = code à 6 chiffres reçu par email
  const [otpStep, setOtpStep] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendInfo, setResendInfo] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRef = useRef(null);
  const { login, loginOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect compact/sidepanel mode
  const params = new URLSearchParams(location.search);
  const isCompact = params.get('mode') === 'sidepanel' || window.self !== window.top;

  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Auto-focus the code input when entering the 2FA step
  useEffect(() => {
    if (otpStep && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [otpStep]);

  // Décompte du bouton "Renvoyer le code"
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isLoading) return;
    setError('');
    setIsLoading(true);

    const result = await login(email, password);

    if (result.success && result.otpRequired) {
      // Un code a été envoyé par email — passage à la 2e étape (pas de nav).
      setChallengeToken(result.challengeToken);
      setOtpEmail(result.email || email);
      setOtpStep(true);
      setResendCooldown(30);
      setIsLoading(false);
      return;
    }

    if (result.success) {
      if (isCompact) sessionStorage.setItem('medipocket_compact', 'true');
      navigate(isCompact ? '/?mode=sidepanel' : '/');
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handleOtpSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isLoading) return;
    setError('');
    setIsLoading(true);
    const result = await loginOtp(challengeToken, otpCode.trim());
    if (result.success) {
      if (isCompact) sessionStorage.setItem('medipocket_compact', 'true');
      navigate(isCompact ? '/?mode=sidepanel' : '/');
    } else {
      setError(result.error || 'Code incorrect');
      setOtpCode('');
      setTimeout(() => otpInputRef.current?.focus(), 50);
    }
    setIsLoading(false);
  };

  const handleOtpResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setResendInfo('');
    const result = await resendOtp(challengeToken);
    if (result.success) {
      setResendInfo('Un nouveau code vient de vous être envoyé.');
      setResendCooldown(30);
    } else {
      setError(result.error || "Impossible de renvoyer le code.");
    }
  };

  const handleOtpBack = () => {
    setOtpStep(false);
    setOtpCode('');
    setChallengeToken('');
    setResendInfo('');
    setResendCooldown(0);
    setPassword('');
    setError('');
  };

  // ── Compact Side Panel Login ──
  if (isCompact) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="login-page-compact">
        {/* Compact header */}
        <div className="bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-3 flex items-center gap-2.5">
          <div className="w-7 h-7 bg-white/15 rounded-lg flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold font-['Outfit'] text-white tracking-tight">MediPocket</span>
        </div>

        {/* Login form */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-sm">
            <h2 className="text-lg font-medium font-['Outfit'] tracking-tight text-slate-900 mb-1">
              {otpStep ? 'Vérification par email' : 'Connexion'}
            </h2>
            <p className="text-xs text-slate-500 mb-5">
              {otpStep
                ? <>Un code à 6 chiffres a été envoyé à <strong>{otpEmail}</strong>. Valable 10 minutes.</>
                : 'Connectez-vous pour utiliser MediPocket'}
            </p>

            <div className="space-y-3.5">
              {error && (
                <div role="alert" aria-live="assertive" className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-xl text-red-600 text-xs" data-testid="login-error">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {otpStep ? (
                <form onSubmit={handleOtpSubmit} className="space-y-3.5">
                  <div className="space-y-1.5">
                    <Label htmlFor="otp-code-compact" className="text-slate-700 font-medium text-xs flex items-center gap-1.5">
                      <KeyRound className="w-3.5 h-3.5 text-indigo-500" />Code reçu par email
                    </Label>
                    <Input
                      id="otp-code-compact"
                      ref={otpInputRef}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      pattern="[0-9]*"
                      placeholder="123 456"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      className="h-10 rounded-lg bg-slate-50 border-slate-200 text-base tracking-widest placeholder:text-slate-400 focus:bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/40"
                      data-testid="login-otp-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading || !otpCode.trim()}
                    className="w-full h-9 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white font-medium text-sm shadow-sm transition-all"
                    data-testid="login-otp-submit-btn"
                  >
                    {isLoading ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Vérification...</>) : 'Vérifier'}
                  </Button>
                  {resendInfo && <p role="alert" aria-live="polite" className="text-[11px] text-emerald-600 text-center">{resendInfo}</p>}
                  <button
                    type="button"
                    onClick={handleOtpResend}
                    disabled={resendCooldown > 0}
                    className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline w-full text-center disabled:text-slate-400 disabled:no-underline"
                    data-testid="login-otp-resend"
                  >
                    {resendCooldown > 0 ? `Renvoyer le code (${resendCooldown}s)` : 'Renvoyer le code'}
                  </button>
                  <button
                    type="button"
                    onClick={handleOtpBack}
                    className="text-xs text-slate-500 hover:text-slate-700 hover:underline w-full text-center"
                    data-testid="login-otp-back"
                  >
                    ← Revenir à l'étape précédente
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3.5">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-slate-700 font-medium text-xs">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      autoFocus
                      placeholder="docteur@exemple.fr"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-9 rounded-lg bg-slate-50 border-slate-200 text-sm placeholder:text-slate-400 focus:bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/40"
                      data-testid="login-email-input"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-slate-700 font-medium text-xs">Mot de passe</Label>
                      <Link to="/forgot-password" data-testid="login-forgot-password-link"
                        className="text-[11px] text-indigo-600 hover:text-indigo-700 hover:underline font-medium">
                        Mot de passe oublié&nbsp;?
                      </Link>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-9 rounded-lg bg-slate-50 border-slate-200 text-sm placeholder:text-slate-400 focus:bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/40"
                      data-testid="login-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading || !email.trim() || !password.trim()}
                    className="w-full h-9 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white font-medium text-sm shadow-sm transition-all"
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Connexion...</>
                    ) : (
                      'Se connecter'
                    )}
                  </Button>
                </form>
              )}

              <div className="flex items-center justify-center gap-1 pt-2">
                <Lock className="w-2.5 h-2.5 text-slate-400" />
                <span className="text-[11px] text-slate-500">Données anonymisées · Aucun stockage patient · HTTPS</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Full Desktop Login ──
  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - Brand */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden bg-slate-900">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/90 via-violet-600/80 to-slate-900/95" />
        
        {/* Decorative grid pattern */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }} />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold font-['Outfit'] text-white tracking-tight">MediPocket</span>
          </div>

          {/* Hero content */}
          <div className="max-w-md">
            <h1 className="text-4xl sm:text-5xl font-semibold font-['Outfit'] text-white tracking-tight leading-[1.1] mb-6">
              Transcription médicale intelligente
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-10">
              Dictez vos observations, l'IA structure vos notes médicales en temps réel. Conçu pour les professionnels de santé.
            </p>

            {/* Feature pills */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Mic className="w-4 h-4" />
                </div>
                <span className="text-sm">Dictée vocale avec Whisper</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <span className="text-sm">Notes structurées automatiquement</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4" />
                </div>
                <span className="text-sm">Alertes de sécurité et interactions</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="text-xs text-white/30">MediPocket — Conçu pour la médecine générale - Dr SEBAG</p>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="w-full lg:w-[55%] flex items-center justify-center p-6 sm:p-12 bg-[#f8fafc]">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 lg:hidden mb-10">
            <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold font-['Outfit'] bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">MediPocket</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-medium font-['Outfit'] tracking-tight text-slate-900 mb-2">
              {otpStep ? 'Vérification par email' : 'Connexion'}
            </h2>
            <p className="text-base text-slate-500">
              {otpStep
                ? 'Nouvelle connexion à confirmer par un code envoyé par email.'
                : 'Entrez vos identifiants pour accéder à votre espace'}
            </p>
          </div>

          <div className="space-y-5">
            {error && (
              <div role="alert" aria-live="assertive" className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {otpStep ? (
              <form onSubmit={handleOtpSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="otp-code-desktop" className="text-slate-700 font-medium text-sm flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-indigo-500" />Code reçu par email
                  </Label>
                  <Input
                    id="otp-code-desktop"
                    ref={otpInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    pattern="[0-9]*"
                    placeholder="123 456"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 text-slate-900 text-lg tracking-widest placeholder:text-slate-400 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 transition-all"
                    data-testid="login-otp-input-desktop"
                  />
                  <p className="text-xs text-slate-500">
                    Un code à 6 chiffres a été envoyé à <strong>{otpEmail}</strong>. Valable 10 minutes.
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={isLoading || !otpCode.trim()}
                  className="w-full h-12 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white font-medium text-base shadow-sm hover:shadow-lg hover:shadow-indigo-500/25 transition-all duration-200 mt-2"
                  data-testid="login-otp-submit-btn-desktop"
                >
                  {isLoading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Vérification...</>
                  ) : ('Vérifier')}
                </Button>
                {resendInfo && <p role="alert" aria-live="polite" className="text-xs text-emerald-600 text-center">{resendInfo}</p>}
                <button
                  type="button"
                  onClick={handleOtpResend}
                  disabled={resendCooldown > 0}
                  className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline w-full text-center disabled:text-slate-400 disabled:no-underline"
                  data-testid="login-otp-resend-desktop"
                >
                  {resendCooldown > 0 ? `Renvoyer le code (${resendCooldown}s)` : 'Renvoyer le code'}
                </button>
                <button
                  type="button"
                  onClick={handleOtpBack}
                  className="text-sm text-slate-500 hover:text-slate-700 hover:underline w-full text-center"
                  data-testid="login-otp-back-desktop"
                >
                  ← Revenir à l'étape précédente
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium text-sm">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    autoFocus
                    placeholder="docteur@exemple.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 transition-all"
                    data-testid="login-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700 font-medium text-sm">Mot de passe</Label>
                    <Link to="/forgot-password" data-testid="login-forgot-password-link-desktop"
                      className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline font-medium">
                      Mot de passe oublié&nbsp;?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 transition-all"
                    data-testid="login-password-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={isLoading || !email.trim() || !password.trim()}
                  className="w-full h-12 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white font-medium text-base shadow-sm hover:shadow-lg hover:shadow-indigo-500/25 transition-all duration-200 mt-2"
                  data-testid="login-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Connexion en cours...
                    </>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </form>
            )}

            <div className="flex items-center justify-center gap-1.5 pt-3 pb-1">
              <Lock className="w-3 h-3 text-slate-400" />
              <span className="text-[11px] text-slate-500">Données anonymisées · Aucun stockage patient · HTTPS</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
